/**
 * Shared DevLogger utility for consistent console logging across different modules.
 * Provides a factory function to create loggers with persistent enable/expanded state.
 */

/**
 * Timestamp style for console output
 */
const TIMESTAMP_STYLE = 'color: #888; font-size: 11px;'

/**
 * Get formatted timestamp
 * @returns {string}
 */
const getTimestamp = () => {
  return new Date().toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  })
}

/**
 * Safely check if we're in a browser environment
 * @returns {boolean}
 */
const isBrowser = () =>
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'

/**
 * Safely get item from localStorage
 * @param {string} key
 * @returns {string|null}
 */
const getStorageItem = (key) => {
  if (isBrowser()) {
    return localStorage.getItem(key)
  }
  return null
}

/**
 * Safely set item in localStorage
 * @param {string} key
 * @param {string} value
 */
const setStorageItem = (key, value) => {
  if (isBrowser()) {
    localStorage.setItem(key, value)
  }
}

/**
 * Flatten an object to a single level with dot notation keys
 *
 * @param {object} obj - Object to flatten
 * @param {string} prefix - Prefix for nested keys
 * @param {number} maxDepth - Maximum depth to flatten
 * @returns {object}
 */
export const flattenObject = (obj, prefix = '', maxDepth = 2) => {
  const result = {}

  if (!obj || typeof obj !== 'object' || maxDepth <= 0) {
    return result
  }

  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value, newKey, maxDepth - 1))
    } else {
      result[newKey] = value
    }
  }

  return result
}

/**
 * Format a value for display
 *
 * @param {any} value
 * @returns {string}
 */
export const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '-'
  }
  if (typeof value === 'object') {
    const str = JSON.stringify(value)
    return str.length > 80 ? str.substring(0, 77) + '...' : str
  }
  return String(value)
}

/**
 * Create a DevLogger instance for a specific module
 *
 * @param {object} config - Logger configuration
 * @param {string} config.name - Display name for the logger (e.g., 'EventBus', 'Statsig')
 * @param {string} config.badgeColor - Background color for the badge
 * @param {string} config.storageKeyPrefix - Prefix for localStorage keys
 * @param {boolean} config.defaultEnabled - Whether logging is enabled by default
 * @param {string} config.windowDebugFlag - Window flag for debug mode (optional)
 * @param {string} config.windowExpandedFlag - Window flag for expanded mode (optional)
 * @returns {object} Logger instance with methods
 */
export function createDevLogger({
  name,
  badgeColor,
  storageKeyPrefix,
  defaultEnabled = false,
  windowDebugFlag = null,
  windowExpandedFlag = null,
}) {
  const STORAGE_KEY = `${storageKeyPrefix}Logging`
  const EXPANDED_STORAGE_KEY = `${storageKeyPrefix}LoggingExpanded`

  const BADGE_STYLE = `background: ${badgeColor}; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;`
  const TEXT_STYLE = `color: ${badgeColor}; font-weight: bold;`

  /**
   * Get logging enabled state from storage
   * @returns {boolean}
   */
  const getEnabled = () => {
    const stored = getStorageItem(STORAGE_KEY)
    if (stored === null) {
      return defaultEnabled
    }
    return stored === 'true'
  }

  /**
   * Get expanded state from storage
   * @returns {boolean}
   */
  const getExpanded = () => {
    return getStorageItem(EXPANDED_STORAGE_KEY) === 'true'
  }

  /**
   * Set logging enabled state
   * @param {boolean} enabled
   */
  const setEnabled = (enabled) => {
    setStorageItem(STORAGE_KEY, enabled ? 'true' : 'false')
    if (isBrowser() && windowDebugFlag) {
      window[windowDebugFlag] = enabled
    }
  }

  /**
   * Set expanded state
   * @param {boolean} expanded
   */
  const setExpanded = (expanded) => {
    setStorageItem(EXPANDED_STORAGE_KEY, expanded ? 'true' : 'false')
    if (isBrowser() && windowExpandedFlag) {
      window[windowExpandedFlag] = expanded
    }
  }

  /**
   * Check if logging is currently enabled
   * @returns {boolean}
   */
  const isEnabled = () => {
    if (isBrowser() && windowDebugFlag) {
      return !!window[windowDebugFlag]
    }
    return getEnabled()
  }

  /**
   * Check if logs should be expanded
   * @returns {boolean}
   */
  const isExpanded = () => {
    if (isBrowser() && windowExpandedFlag) {
      return !!window[windowExpandedFlag]
    }
    return getExpanded()
  }

  /**
   * Initialize window flags from storage (call on load)
   */
  const initWindowFlags = () => {
    if (isBrowser()) {
      if (windowDebugFlag) {
        window[windowDebugFlag] = getEnabled()
      }
      if (windowExpandedFlag) {
        window[windowExpandedFlag] = getExpanded()
      }
    }
  }

  /**
   * Log a message to the console with badge styling
   *
   * @param {string} message - Message to display after badge
   * @param {object} options - Logging options
   * @param {string} options.textStyle - Custom text style (defaults to badge color)
   * @param {object} options.data - Data to show in table format
   * @param {any} options.fullData - Full data object to log
   * @param {boolean} options.forceExpand - Force expand this log entry
   */
  const log = (message, { textStyle, data, fullData, forceExpand } = {}) => {
    if (!isEnabled()) {
      return
    }

    const expanded = forceExpand || isExpanded()
    const timestamp = getTimestamp()
    const style = textStyle || TEXT_STYLE

    /* eslint-disable no-console */
    const logFn = expanded ? console.group : console.groupCollapsed
    logFn(
      `%c[${name}]%c ${message} %c${timestamp}`,
      BADGE_STYLE,
      style,
      TIMESTAMP_STYLE,
    )

    if (data && typeof data === 'object') {
      const flattened = flattenObject(data)
      const summary = {}
      for (const [key, value] of Object.entries(flattened)) {
        summary[key] = formatValue(value)
      }
      console.table(summary)
    }

    if (fullData !== undefined) {
      console.log('Full event data:', fullData)
    }

    console.groupEnd()
    /* eslint-enable no-console */
  }

  // Initialize window flags on creation
  initWindowFlags()

  return {
    getEnabled,
    setEnabled,
    getExpanded,
    setExpanded,
    isEnabled,
    isExpanded,
    initWindowFlags,
    log,
    // Export styles for custom usage
    BADGE_STYLE,
    TEXT_STYLE,
    TIMESTAMP_STYLE,
  }
}
